// O botão "Assinar" da área de um Mentor (Círculo do Mentor, 14/10/2026).
// Recebe o `compra` de GET /api/mentores/:slug ({ sku, pacotes, baixar_url }).
//   · no app (iOS/Android): compra pela loja via RevenueCat e, ao concluir,
//     pede ao servidor para sincronizar — o acesso abre na mesma hora, sem
//     esperar o webhook;
//   · na web: a assinatura é feita pelo app — link para /baixar.
// A página do Mentor ainda não existe (espera o Wesley escolher a navegação);
// este componente fica pronto para ela.
import React, { useState } from 'react';
import { isNativeApp } from '../platform';
import { assinarMentor, identidadeRCPendente, restorePurchasesRC } from '../revenuecat';
import { api } from '../../services/apiV2';
import { usoAcao } from '../uso';

export interface CompraDoMentor {
  sku: string;
  pacotes: { mensal: string; anual: string };
  baixar_url: string;
}

export const AssinarMentor: React.FC<{
  session: any;
  /** O servidor confere antes da loja: sem série publicada, 409 (QA final, D2). */
  slug: string;
  mentorNome: string;
  compra: CompraDoMentor;
  /** Preços do Círculo para mostrar (a loja mostra o preço final na compra). */
  precos?: { mensal?: string; anual?: string };
  onAssinou?: () => void;
}> = ({ session, slug, mentorNome, compra, precos, onAssinou }) => {
  const [periodo, setPeriodo] = useState<'mensal' | 'anual'>('anual');
  const [estado, setEstado] = useState<'pronto' | 'comprando' | 'liberando'>('pronto');
  const [erro, setErro] = useState<string | null>(null);

  if (!isNativeApp()) {
    return (
      <div data-assinar-mentor="web" style={{
        padding: 18, borderRadius: 16, border: '1px solid var(--line)', background: 'var(--paper-2)',
        color: 'var(--ink-2)', fontSize: 14.5, lineHeight: 1.55,
      }}>
        A assinatura de {mentorNome} é feita pelo app do Forjamente, no iPhone ou no Android.
        <a href={compra.baixar_url || '/baixar'} style={{
          display: 'inline-block', marginTop: 12, padding: '11px 18px', borderRadius: 999,
          background: 'var(--ember)', color: 'var(--paper)', fontWeight: 600, textDecoration: 'none',
        }}>Baixar o app</a>
      </div>
    );
  }

  const assinar = async () => {
    usoAcao('assinar_loja', { slug, periodo });
    setErro(null);
    setEstado('comprando');
    try {
      await api.podeAssinarMentor(session, slug);
    } catch (e: any) {
      setEstado('pronto');
      setErro(e?.status === 409 ? e.message : 'Não deu para abrir a assinatura agora. Confira a conexão e tente de novo.');
      return;
    }
    const r = await assinarMentor(periodo === 'anual' ? compra.pacotes.anual : compra.pacotes.mensal);
    if (!r.ok) {
      setEstado('pronto');
      if (!r.cancelled) {
        setErro(r.error === 'produto_indisponivel'
          ? 'A assinatura não está disponível na loja agora. Tente de novo em instantes.'
          : 'Não foi possível concluir a assinatura. Nada foi cobrado se a loja não confirmou.');
      }
      return;
    }
    // A loja confirmou: o servidor grava a assinatura do Mentor agora mesmo.
    setEstado('liberando');
    await api.syncRcPlan(session);
    setEstado('pronto');
    onAssinou?.();
  };

  // A Apple exige restaurar compras em app com assinatura (troca de aparelho).
  const restaurar = async () => {
    setErro(null);
    setEstado('liberando');
    await restorePurchasesRC();
    await api.syncRcPlan(session);
    setEstado('pronto');
    onAssinou?.();
  };

  const opcao = (p: 'mensal' | 'anual', rotulo: string) => (
    <button key={p} onClick={() => setPeriodo(p)} aria-pressed={periodo === p} style={{
      flex: 1, padding: '12px 10px', borderRadius: 14, fontFamily: 'inherit', cursor: 'pointer',
      border: periodo === p ? '1.5px solid var(--ember)' : '1px solid var(--line-strong)',
      background: periodo === p ? 'var(--ember-soft)' : 'transparent', color: 'var(--ink)',
      fontSize: 14, fontWeight: 600, textAlign: 'center',
    }}>
      {rotulo}
      {precos?.[p] && <div style={{ fontSize: 12.5, fontWeight: 400, color: 'var(--ink-3)', marginTop: 2 }}>{precos[p]}</div>}
    </button>
  );

  return (
    <div data-assinar-mentor="app" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        {opcao('mensal', 'Mensal')}
        {opcao('anual', 'Anual')}
      </div>
      <button onClick={assinar} disabled={estado !== 'pronto'} style={{
        padding: '15px 20px', borderRadius: 999, border: 'none', fontFamily: 'inherit',
        background: 'var(--ember)', color: 'var(--paper)', fontSize: 15.5, fontWeight: 600,
        cursor: estado === 'pronto' ? 'pointer' : 'default', opacity: estado === 'pronto' ? 1 : 0.7,
      }}>
        {estado === 'comprando' ? (identidadeRCPendente() ? 'Preparando…' : 'Abrindo a loja…')
          : estado === 'liberando' ? 'Liberando o acesso…'
            : `Assinar ${mentorNome}`}
      </button>
      {erro && <div style={{ fontSize: 13, color: '#C03A2A', lineHeight: 1.5 }}>{erro}</div>}
      <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
        Cobrança e cancelamento pela loja do seu aparelho. Renova automaticamente até você cancelar.
      </div>
      <button onClick={restaurar} disabled={estado !== 'pronto'} style={{
        alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
        color: 'var(--ink-3)', fontSize: 12.5, textDecoration: 'underline', fontFamily: 'inherit', cursor: 'pointer',
      }}>Restaurar compras</button>
    </div>
  );
};

export default AssinarMentor;
